import { useCallback, useEffect, useRef, useState } from 'react'
import s from './styles.module.scss'
import { head, timeFormat } from 'helpers'
import { Alert, ImageTag, InfiniteScroll, Layouts, Loader, Modal, NoData, TextArea } from 'components'
import * as api from 'api'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST
const PER_PAGE = 10

const getPhotoUrl = (logo) => (logo ? `${IMAGE_HOST}${logo}` : null)

export default function Main() {
  const [users, setUsers] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [pageNo, setPageNo] = useState(1)
  const [loading, setLoading] = useState(false)
  const [rejectionMessages, setRejectionMessages] = useState({})
  const [actionUserId, setActionUserId] = useState('')
  const processing = useRef(false)

  const getUsers = useCallback(async (filter, currPage) => {
    if (processing.current) return
    processing.current = true
    setLoading(true)
    setPageNo(currPage + 1)

    try {
      const fetchUsers = await api.admin.user.fetchPendingAadhaarVerifications({
        perPage: PER_PAGE,
        pageNo: currPage,
      })

      if (fetchUsers.code === 200) {
        const items = fetchUsers.payload?.users || []
        const count = fetchUsers.payload?.total || 0
        setUsers(prev => (currPage === 1 ? items : prev.concat(items)))
        setTotal(count)
        setTotalPages(count ? Math.ceil(count / PER_PAGE) : 0)
      } else {
        Alert.error(fetchUsers.message)
      }
    } catch (err) {
      Alert.error(err?.response?.data?.message || err.message)
    } finally {
      setLoading(false)
      processing.current = false
    }
  }, [])

  const updateRejectionMessage = (userId, value) => {
    if (value.trim().split(/\s+/).length > 200) return
    setRejectionMessages(prev => ({ ...prev, [userId]: value }))
  }

  const updateAadhaarVerificationStatus = async (user, isApproved) => {
    const rejectionMessage = (rejectionMessages[user._id] || '').trim()

    if (!isApproved && !rejectionMessage) {
      Alert.warn('Rejection message is required')
      return
    }

    if (actionUserId) return
    setActionUserId(user._id)

    try {
      const updateUser = await api.admin.user.updateAadhaarVerificationStatus({
        id: user._id,
        isApproved,
        rejectionMessage: isApproved ? '' : rejectionMessage,
      })

      if (updateUser.code === 201) {
        setUsers(prev => prev.filter(u => u._id !== user._id))
        setTotal(prev => Math.max(0, prev - 1))
        setRejectionMessages(prev => {
          const next = { ...prev }
          delete next[user._id]
          return next
        })
        Alert.success(updateUser.message)
      } else {
        Alert.error(updateUser.message)
      }
    } catch (err) {
      Alert.error(err?.response?.data?.message || err.message)
    } finally {
      setActionUserId('')
    }
  }

  useEffect(() => {
    head({ title: 'Aadhaar Verification | BCN' })
  }, [])

  useEffect(() => {
    if (!users.length) {
      getUsers('', 1)
    }
  }, [getUsers, users.length])

  const fullName = user => `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unnamed user'

  return (
    <div className={s.page}>
      <Layouts.Classic title="Aadhaar Verification" />

      <div className={s.hero}>
        <p className={s.heroText}>
          Review uploaded Aadhaar cards and approve or reject verification. Approved users receive two verification ticks on their profile.
        </p>

        <div className={s.statsRow}>
          <div className={s.statCard}>
            <span className={s.statLabel}>Pending verifications</span>
            <strong className={s.statValue}>{total}</strong>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>Loaded on screen</span>
            <strong className={s.statValue}>{users.length}</strong>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>Action required</span>
            <strong className={s.statValue}>Review Aadhaar</strong>
          </div>
        </div>
      </div>

      {loading && !users.length ? (
        <div className={s.loaderWrap}>
          <Loader color="var(--c-primary)" colorText="var(--c-primary)" />
        </div>
      ) : !users.length ? (
        <div className={s.emptyCard}>
          <NoData />
          <p>There are no pending Aadhaar verifications right now. All caught up.</p>
        </div>
      ) : (
        <InfiniteScroll next={getUsers} filter="" currentPage={pageNo} hasMore={pageNo <= totalPages} inLayout>
          <div className={s.userList}>
            {users.map(user => {
              const photoUrl = getPhotoUrl(user.logo)
              const aadhaarUrl = user.aadhaarCard ? `${IMAGE_HOST}${user.aadhaarCard}` : null
              const isProcessing = actionUserId === user._id
              const name = fullName(user)

              return (
                <article key={user._id} className={s.userCard}>
                  <div className={s.cardHeader}>
                    <div className={s.userIdentity}>
                      {photoUrl ? (
                        <div className={s.userPhotoWrap}>
                          <ImageTag src={photoUrl} alt={name} />
                        </div>
                      ) : (
                        <div className={s.userPhotoPlaceholder}>
                          <span className="material-icons-outlined">person</span>
                        </div>
                      )}
                      <div>
                        <h2>{name}</h2>
                        <p className={s.userMeta}>
                          {[user.city, user.state].filter(Boolean).join(', ') || 'Location not set'}
                          {user.phoneNo ? ` · +91-${user.phoneNo}` : ''}
                        </p>
                        <span className={s.badgePending}>Pending verification</span>
                      </div>
                    </div>

                    <div className={s.uploadedOn}>
                      <span className="material-icons-outlined">schedule</span>
                      <div>
                        <span className={s.uploadedLabel}>Uploaded</span>
                        <strong>{timeFormat(user.createdAt, true)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className={s.detailGrid}>
                    {user.gender ? (
                      <div className={s.detailItem}>
                        <span className={s.detailLabel}>Gender</span>
                        <strong>{user.gender}</strong>
                      </div>
                    ) : null}
                    <div className={s.detailItem}>
                      <span className={s.detailLabel}>Date of birth</span>
                      <strong>{user.dob ? new Date(user.dob).toLocaleDateString() : 'N/A'}</strong>
                    </div>
                    {user.profession ? (
                      <div className={s.detailItem}>
                        <span className={s.detailLabel}>Profession</span>
                        <strong>{user.profession}</strong>
                      </div>
                    ) : null}
                    {user.gotra ? (
                      <div className={s.detailItem}>
                        <span className={s.detailLabel}>Gotra</span>
                        <strong>{user.gotra}</strong>
                      </div>
                    ) : null}
                    {user.email ? (
                      <div className={s.detailItem}>
                        <span className={s.detailLabel}>Email</span>
                        <a href={`mailto:${user.email}`} className={s.detailLink}>
                          {user.email}
                        </a>
                      </div>
                    ) : null}
                    {user.phoneNo ? (
                      <div className={s.detailItem}>
                        <span className={s.detailLabel}>Phone</span>
                        <a href={`tel:+91${user.phoneNo}`} className={s.detailLink}>
                          +91-{user.phoneNo}
                        </a>
                      </div>
                    ) : null}
                    {user.address ? (
                      <div className={`${s.detailItem} ${s.fullWidth}`}>
                        <span className={s.detailLabel}>Address</span>
                        <strong>{user.address}</strong>
                      </div>
                    ) : null}
                  </div>

                  {aadhaarUrl ? (
                    <div className={s.aadhaarSection}>
                      <div className={s.sectionTitle}>
                        <span className="material-icons-outlined">badge</span>
                        <h3>Aadhaar Card</h3>
                      </div>
                      <a
                        href={aadhaarUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={s.aadhaarPreview}
                      >
                        <ImageTag src={aadhaarUrl} alt={`Aadhaar card for ${name}`} />
                        <div className={s.aadhaarOverlay}>
                          <span className="material-icons-outlined">open_in_new</span>
                          View full size
                        </div>
                      </a>
                    </div>
                  ) : null}

                  <div className={s.rejectionSection}>
                    <TextArea.Classic
                      label="Rejection message (required if rejecting, max 200 words)"
                      iconLeft="description"
                      placeholder="Enter reason for rejection..."
                      value={rejectionMessages[user._id] || ''}
                      onChange={e => updateRejectionMessage(user._id, e.target.value)}
                    />
                  </div>

                  <div className={s.cardActions}>
                    <button
                      type="button"
                      className={s.approveBtn}
                      disabled={isProcessing}
                      onClick={() =>
                        Modal.Confirm(
                          `Approve Aadhaar verification for ${name}?`,
                          () => updateAadhaarVerificationStatus(user, true)
                        )
                      }
                    >
                      <span className="material-icons-outlined">verified</span>
                      {isProcessing ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      className={s.rejectBtn}
                      disabled={isProcessing}
                      onClick={() =>
                        Modal.Confirm(
                          `Reject Aadhaar verification for ${name}?`,
                          () => updateAadhaarVerificationStatus(user, false)
                        )
                      }
                    >
                      <span className="material-icons-outlined">close</span>
                      {isProcessing ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        </InfiniteScroll>
      )}

      {!!loading && !!users.length && (
        <div className={s.loadingMore}>
          <Loader color="var(--c-primary)" colorText="var(--c-primary)" />
        </div>
      )}
    </div>
  )
}
